import React, { useEffect, useState } from 'react'
import { userRoleOptions } from 'components/permissions/utils'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import api from 'helpers/api'
import { IAccessPolicy, IUserObject } from 'types'
import { UserRoles } from 'app-constants'
import { validateSourceUrl } from 'helpers/utils'
import Select from 'components/select'
import Skeleton from 'components/skeleton'

interface Props {
  policy: IAccessPolicy
  onUpdatePolicy: (policy: IAccessPolicy, role: UserRoles) => void
  excludedPermissionCategories?: UserRoles[]
}

const UserPermissions: React.FC<Props> = ({ policy, onUpdatePolicy, excludedPermissionCategories }) => {
  const { displayErrorMessage } = useApplicationStore()
  const { user } = useAuth()
  const [userInfo, setUserInfo] = useState<IUserObject>()
  const [loaded, setLoaded] = useState(false)

  const fetchUser = async () => {
    const firebaseUserId = policy.accessAttribute.user?.firebaseUserId
    if (!firebaseUserId) {
      setLoaded(true)
      return
    }
    try {
      const response = await api.getUser(firebaseUserId)
      setUserInfo(response.data)
      setLoaded(true)
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [policy])

  return (
    <div className="flex items-center cursor-pointer rounded-lg border-box select-none border border-divider transition-all p-6 min-h-[90px] mt-0 mb-2.5 mx-[5px]">
      {!loaded && (
        <div className="flex w-full">
          <Skeleton className="rounded-full mr-2.5 w-[50px] h-[50px]" />
          <div className="flex justify-center flex-col h-full">
            <Skeleton className="mb-[5px] w-[150px] h-[17px]" />
            <Skeleton className="mb-[5px] w-[300px] h-[17px]" />
          </div>
          <div className="ml-auto w-[130px]">
            <Skeleton className="mb-[5px] w-[130px] h-[18.75px]" />
          </div>
        </div>
      )}

      {loaded && userInfo && (
        <div className="contents">
          <img
            className="object-cover rounded-full mr-2.5 w-[50px] h-[50px]"
            alt={userInfo.name}
            src={validateSourceUrl(userInfo.profilePicture)}
          />

          <div className="flex justify-center flex-col h-full">
            <div className="font-bold mb-[5px]">
              {user && userInfo.firebaseUserId === user!.firebaseUserId ? 'Me' : userInfo.name}
            </div>
            <div>
              Set permissions for this user (
              <a href={`/profile/${userInfo.firebaseUserId}/information`} target="_blank" rel="noopener noreferrer">
                view profile
              </a>
              ).
            </div>
          </div>
          <div className="ml-auto w-[130px]">
            <Select
              options={userRoleOptions
                .filter(
                  (userRole) => !excludedPermissionCategories || !excludedPermissionCategories.includes(userRole.value)
                )
                .map((option) => ({
                  value: String(option.value),
                  label: option.label
                }))}
              optionsSelected={[
                String(
                  policy
                    ? excludedPermissionCategories && excludedPermissionCategories.includes(policy.role)
                      ? ''
                      : policy.role
                    : -1
                )
              ]}
              onOptionClick={(option) => onUpdatePolicy(policy, parseInt(option))}
              searchable={false}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default UserPermissions
